import { useCallback, useEffect } from 'react';

import { localeOption } from 'primereact/api';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ConnectionsType } from '@/app/slices/connections';
import {
  FreshShipmentActions,
  FreshShipmentType,
} from '@/app/slices/freshShipment';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import usePageTitle from '@/components/hooks/usePageTitle';
import { FertilizationQCResponse, ShipmentGroup } from '@/rtk-query.gql';
import { useFreshShipmentCacheInvalidation } from '@/services/fresh-shipment/hooks';
import { useSites } from '@/services/sites';
import { formatURLDate } from '@/utils';

type Props = {
  className: string;
  totals: {
    qual: number;
    low: number;
    ship: number;
    estCount: number;
    totalSelectedShipped: number;
  };
  checkQcLoaded(fertQc: FertilizationQCResponse): void;
  fertQc: FertilizationQCResponse;
  setSelectedShipmentGroupsLocal(groups: ShipmentGroup[] | []): void;
};

export const FreshShipmentSelector = ({
  totals,
  checkQcLoaded,
  fertQc,
  setSelectedShipmentGroupsLocal,
}: Props) => {
  const { setTitle } = usePageTitle();
  const invalidateCache = useFreshShipmentCacheInvalidation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showForm, isCsr } = useSelector(
    (state: { freshShipment: FreshShipmentType }) => state.freshShipment,
  );
  const { currentRegion } = useSelector(
    (state: { connections: ConnectionsType }) => state.connections,
  );

  const { data: sitesData } = useSites({ filter: 'fertilization' });
  const sites = sitesData?.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const [params, setParams] = useSearchParams();
  const date = params.get('date');
  const siteId = params.get('sId');
  const groupId = params.get('group-id');
  const title = params.get('title');

  useEffect(() => {
    if (title) {
      setTitle(title);
      document.title = title;
    }
  }, [setTitle, title]);

  const handleBackToShipGroups = useCallback(() => {
    checkQcLoaded(fertQc);
    setTitle('Fresh Shipment');
    document.title = 'Fresh Shipment';
    const newParams = new URLSearchParams(params);
    newParams.delete('group-id');
    newParams.delete('title');
    setParams(newParams);
    dispatch({
      type: FreshShipmentActions.UPDATE_SHOW_FORM,
      showForm: false,
    });

    const queryString = newParams.toString();
    navigate(`/fresh-shipment?${queryString}`);
  }, [checkQcLoaded, dispatch, fertQc, navigate, params, setParams, setTitle]);

  const handleDateChange = useCallback(
    (e: { value: Date | null | undefined }) => {
      const selectedDate = e.value;
      checkQcLoaded(fertQc);
      setTitle('Fresh Shipment');
      document.title = 'Fresh Shipment';

      const newParams = new URLSearchParams(params);

      if (selectedDate) {
        newParams.set('date', formatURLDate(selectedDate));
      } else {
        newParams.delete('date');
      }

      newParams.delete('group-id');
      newParams.delete('title');

      setSelectedShipmentGroupsLocal([]);
      setParams(newParams);

      invalidateCache([
        'FreshShipmentList',
        'QcFreshShipment',
        'ShipGroupList',
        'FertilizationListFreshShipment',
      ]);
    },
    [
      checkQcLoaded,
      fertQc,
      params,
      setParams,
      setSelectedShipmentGroupsLocal,
      setTitle,
      invalidateCache,
    ],
  );

  const handleSiteChange = useCallback(
    (e: DropdownChangeEvent) => {
      setTitle('Fresh Shipment');
      document.title = 'Fresh Shipment';
      const newParams = new URLSearchParams(params);
      newParams.delete('title');
      if (e.value) {
        newParams.set('sId', e.value);
      } else {
        newParams.delete('sId');
      }

      setSelectedShipmentGroupsLocal([]);
      setParams(newParams);

      invalidateCache([
        'FreshShipmentList',
        'QcFreshShipment',
        'ShipGroupList',
        'FertilizationListFreshShipment',
      ]);
    },
    [
      params,
      setParams,
      setSelectedShipmentGroupsLocal,
      setTitle,
      invalidateCache,
    ],
  );

  return (
    <>
      {showForm && (
        <Button
          icon="pi pi-arrow-left"
          className="back-to-ship-groups"
          onClick={handleBackToShipGroups}
          severity="secondary"
        />
      )}

      <Calendar
        value={date ? new Date(date) : undefined}
        onChange={handleDateChange}
      />
      <Dropdown
        value={siteId}
        placeholder={localeOption('selectSite', currentRegion.languageCode)}
        onChange={handleSiteChange}
        disabled={!date}
        options={sites}
        filter
      />

      {showForm && (
        <>
          <div className="fresh-shipment__fertilization-totals-wrapper">
            <div className="fresh-shipment__fertilization-totals">
              <strong>Qual: {totals.qual}</strong>
              <strong>Low: {totals.low}</strong>
            </div>
            <div className="fresh-shipment__fertilization-totals">
              <strong>Ship: {totals.ship}</strong>
              <strong>Est: {totals.estCount}</strong>
            </div>
          </div>
          {isCsr && (
            <strong>
              Total Selected Shipped: {totals.totalSelectedShipped}
            </strong>
          )}
        </>
      )}
    </>
  );
};
